import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import BulkUpload from '../components/BulkUpload';
import { Card, CardHeader, CardTitle, CardContent, Button, Tag, Dialog, Select } from '../components/ui';
import Layout from '../components/Layout';

type TabType = 'upload' | 'properties' | 'verification';

interface MasterProperty {
  id: string;
  address: string;
  city: string;
  state: string;
  zip: string;
  property_type: string;
  building_size: number;
  year_built: number;
  percent_leased: number;
  owner_name: string;
  latest_sale_price: number;
  latest_price_per_sf: number;
  latest_cap_rate: number;
  source: string;
  created_at: string;
  primary_image_path?: string | null;
}

/**
 * UI1 - the filters he asked for: *"by property type, size, dollar amount, etc."*
 *
 * ⛔⛔ EVERY ONE OF THESE IS APPLIED SERVER-SIDE. The page holds 50 rows of 7,405; a filter
 * applied to the rows on screen would answer from 0.7% of the database and look like an answer.
 * They travel as query parameters and come back beside an EXACT count.
 */
interface Filters {
  property_type: string;
  city: string;
  min_building_size: string;
  max_building_size: string;
  min_price: string;
  max_price: string;
}

const EMPTY_FILTERS: Filters = {
  property_type: '', city: '', min_building_size: '', max_building_size: '', min_price: '', max_price: '',
};

interface FacetValue { value: string; count: number }
interface Facets { cities: FacetValue[]; propertyTypes: FacetValue[]; total: number }

/** `industrial` -> `Industrial`, `mixed_use` -> `Mixed Use`. The enum is stored snake_case. */
const titleCaseType = (v: string) =>
  v.split('_').map((w) => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');

// Supabase public storage base; primary_image_path comes from the view as
// "properties/<uuid>/hero.jpg" and we prepend the bucket URL.
const SUPABASE_STORAGE_BASE = (import.meta.env.VITE_SUPABASE_URL ||
  'https://qyczsnstfyojbumwznxi.supabase.co') + '/storage/v1/object/public/property-photos/';

export default function DataHub() {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<TabType>('properties');
  const [properties, setProperties] = useState<MasterProperty[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [pagination, setPagination] = useState({ total: 0, limit: 50, offset: 0 });
  const [filters, setFilters] = useState<Filters>(EMPTY_FILTERS);
  const [facets, setFacets] = useState<Facets | null>(null);
  const [showAddModal, setShowAddModal] = useState(false);
  const [addForm, setAddForm] = useState({
    address: '', city: '', state: 'CA', zip: '', property_name: '',
    property_type: '', building_size: '', year_built: '', owner_name: '',
    sale_price: '', cap_rate: '',
  });
  const [addSaving, setAddSaving] = useState(false);
  const [addError, setAddError] = useState<string | null>(null);

  const fetchProperties = useCallback(async (search?: string) => {
    setLoading(true);
    setError(null);
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        setError('Not authenticated. Please log in.');
        return;
      }

      const apiBase = import.meta.env.VITE_API_URL || '/api';
      const params = new URLSearchParams({
        limit: pagination.limit.toString(),
        offset: pagination.offset.toString(),
      });
      if (search) params.append('search', search);
      // Only non-empty filters are sent. An empty box must not become a filter: on the server
      // `Number('')` is 0, so a blank "min price" travelling as `min_price=` would silently
      // become `>= 0` on every request the page makes.
      (Object.keys(EMPTY_FILTERS) as (keyof Filters)[]).forEach((key) => {
        const value = filters[key].trim();
        if (value) params.append(key, value);
      });

      const response = await fetch(`${apiBase}/master-properties?${params}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to fetch properties');
      }

      setProperties(data.properties || []);
      setPagination(prev => ({ ...prev, total: data.pagination?.total || 0 }));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load properties');
    } finally {
      setLoading(false);
    }
  }, [pagination.limit, pagination.offset, filters]);

  useEffect(() => {
    if (activeTab === 'properties') {
      fetchProperties(searchQuery);
    }
  }, [activeTab, fetchProperties, searchQuery]);

  // UI1: the filter values, with the counts that keep the list honest. Fetched ONCE - it pages
  // the whole table server-side, so it is not something to re-run per keystroke.
  useEffect(() => {
    if (activeTab !== 'properties' || facets) return;
    (async () => {
      try {
        const token = localStorage.getItem('token');
        if (!token) return;
        const apiBase = import.meta.env.VITE_API_URL || '/api';
        const res = await fetch(`${apiBase}/master-properties/facets`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const data = await res.json();
        if (data.success) setFacets(data.facets);
      } catch {
        // A missing facet list costs the two dropdowns, never the table. Stay quiet and let the
        // page work with search and the numeric ranges.
      }
    })();
  }, [activeTab, facets]);

  /**
   * ⛔⛔ EVERY FILTER CHANGE RETURNS TO PAGE ONE. Without this, narrowing from 7,405 rows to 3
   * while sitting at offset 200 shows an EMPTY table under "Showing 201 - 250 of 3" - the filter
   * looks broken at exactly the moment it worked.
   */
  const setFilter = (key: keyof Filters, value: string) => {
    setPagination(prev => ({ ...prev, offset: 0 }));
    setFilters(prev => ({ ...prev, [key]: value }));
  };

  const clearFilters = () => {
    setPagination(prev => ({ ...prev, offset: 0 }));
    setFilters(EMPTY_FILTERS);
  };

  const activeFilterCount = (Object.keys(EMPTY_FILTERS) as (keyof Filters)[])
    .filter((k) => filters[k].trim() !== '').length;

  /**
   * ⭐⭐ THE SIZE OF HIS DATABASE IS NOT THE SIZE OF THE RESULT. `pagination.total` is the count of
   * what the FILTER matched, so printing it as "on file" would have the page announce that he
   * owns 302 properties the moment he picks Chino. The unfiltered total comes from the facets.
   */
  const totalOnFile = facets?.total ?? null;

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    setPagination(prev => ({ ...prev, offset: 0 }));
    fetchProperties(searchQuery);
  };

  const handleAddProperty = async () => {
    if (!addForm.address || !addForm.city) {
      setAddError('Address and city are required');
      return;
    }
    setAddSaving(true);
    setAddError(null);
    try {
      const token = localStorage.getItem('token');
      const apiBase = import.meta.env.VITE_API_URL || '/api';
      const body: Record<string, any> = { ...addForm };
      // Convert numeric fields
      if (body.building_size) body.building_size = Number(body.building_size);
      if (body.year_built) body.year_built = Number(body.year_built);
      if (body.sale_price) body.sale_price = Number(body.sale_price);
      if (body.cap_rate) body.cap_rate = Number(body.cap_rate);
      // Remove empty strings
      Object.keys(body).forEach(k => { if (body[k] === '') delete body[k]; });

      const response = await fetch(`${apiBase}/master-properties`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify(body),
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || 'Failed to create property');
      navigate(`/data-hub/${data.property.id}`);
    } catch (err) {
      setAddError(err instanceof Error ? err.message : 'Failed to create property');
    } finally {
      setAddSaving(false);
    }
  };

  const formatCurrency = (value: number | null | undefined) => {
    if (!value) return '-';
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(value);
  };

  const formatNumber = (value: number | null | undefined) => {
    if (!value) return '-';
    return new Intl.NumberFormat('en-US').format(value);
  };

  const tabs: { id: TabType; label: string; icon: React.ReactNode }[] = [
    {
      id: 'properties',
      label: 'Property Database',
      icon: (
        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
        </svg>
      ),
    },
    {
      id: 'upload',
      // DOC2: the format is IN the label. He asked whether documents could be added with this
      // button; they cannot - it parses spreadsheets of property data - and "Bulk Upload" was the
      // wording that made it look like the document door.
      label: 'Bulk Upload (CSV)',
      icon: (
        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
        </svg>
      ),
    },
    {
      id: 'verification',
      label: 'Data Verification',
      icon: (
        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
      ),
    },
  ];

  return (
    <Layout>
      {/* ===== Header (OO5): the Oswald title, one sub line, the actions - on the page ground, no band ===== */}
      <div className="space-y-4">
        <div className="flex flex-wrap items-end gap-x-5 gap-y-3">
          <div className="min-w-0 flex-1 basis-[320px]">
            <h1 className="font-display text-[28px] font-semibold uppercase leading-[1.06] tracking-[-.01em] text-gray-900 sm:text-[32px]">Property Database</h1>
            <div className="mt-[7px] text-[13.5px] leading-snug text-gray-700">
              Your centralized CRE property database
              {totalOnFile ? ` · ${totalOnFile.toLocaleString()} properties on file` : ''}
            </div>
          </div>
          {/* min-w-0 below sm so the row shrinks and WRAPS instead of overflowing the phone (the OO4 finding) */}
          <div className="flex min-w-0 flex-wrap gap-2 sm:shrink-0">
            <Button variant="outline" onClick={() => navigate('/dashboard')}>
              My Deals
            </Button>
            <Button
              variant="outline"
              onClick={() => setShowAddModal(true)}
              leftIcon={
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                </svg>
              }
            >
              Add Property
            </Button>
            <Button
              onClick={() => setActiveTab('upload')}
              leftIcon={
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                </svg>
              }
            >
              {/* DOC2: the format goes in this label too. Fixing only the tab left THIS - the more
                  prominent of the two - still reading like a document door. */}
              Bulk Upload (CSV)
            </Button>
          </div>
        </div>

        {/* Tabs: the red-underline strip (OO3's) */}
        <div role="tablist" aria-label="Tabs" className="-mb-px flex gap-0.5 overflow-x-auto border-b border-gray-200">
          {tabs.map(tab => (
            <button
              key={tab.id}
              role="tab"
              type="button"
              aria-selected={activeTab === tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`-mb-px flex shrink-0 items-center gap-2 border-b-2 px-3.5 py-3 text-[13px] leading-none transition-colors ${
                activeTab === tab.id
                  ? 'border-primary-500 font-semibold text-gray-900'
                  : 'border-transparent font-medium text-gray-500 hover:text-gray-800'
              }`}
            >
              {tab.icon}
              {tab.label}
            </button>
          ))}
        </div>
      </div>

      {/* Content */}
      <div className="mt-4">
        {/* Bulk Upload Tab */}
        {activeTab === 'upload' && (
          <div className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle>Import Property Data</CardTitle>
                <p className="text-xs text-gray-400 mt-0.5">CSV exports merge into the master database - columns auto-detected.</p>
              </CardHeader>
              <CardContent>
                <p className="text-gray-600 mb-4">
                  Upload CSV exports from CoStar, Crexi, LoopNet, or other sources.
                  The system will automatically detect columns and merge with existing data.
                </p>
                {/* DOC2: the answer to "can I add a document with this button?" - no, and here is
                    where it goes instead. It names the real control so the sentence is followable. */}
                <p className="mb-6 rounded-lg border border-gray-200 bg-gray-50 px-4 py-3 text-[12.5px] text-gray-600">
                  <span className="font-semibold text-gray-800">This is for spreadsheets, not documents.</span>{' '}
                  To put a PDF - an OM, a title report, a lease - on a property, open that property
                  and use <span className="font-semibold text-gray-800">Add a document</span> in its
                  Documents section.
                </p>
                <div className="grid grid-cols-4 gap-px rounded-xl border border-gray-200 bg-gray-200 overflow-hidden text-center mb-6">
                  {[
                    { name: 'CoStar', note: 'Full export support' },
                    { name: 'Crexi', note: 'Full export support' },
                    { name: 'LoopNet', note: 'Basic support' },
                    { name: 'Custom', note: 'Map any CSV' },
                  ].map((s) => (
                    <div key={s.name} className="bg-white px-4 py-3.5">
                      <p className="text-lg font-semibold text-gray-900 tracking-tight">{s.name}</p>
                      <p className="text-[10.5px] uppercase tracking-wider text-gray-400 mt-1">{s.note}</p>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>

            <BulkUpload onComplete={() => setActiveTab('properties')} />
          </div>
        )}

        {/* Property Database Tab */}
        {activeTab === 'properties' && (
          <div className="space-y-6">
            <Card>
              {/* The head: title, the count pill, the search - flex-wrap so nothing is clipped by the card's overflow-hidden */}
              <div className="flex flex-wrap items-center gap-2.5 border-b border-gray-150 px-5 py-4">
                <h2 className="text-[15px] font-semibold leading-none text-gray-900">Master Property Database</h2>
                {/* ⛔ The WORD changes with the state. `pagination.total` is the count of what the
                    filter matched; calling that "on file" would tell him his database shrank the
                    moment he picked a city. Unfiltered, the two numbers are the same anyway. */}
                <span className="rounded-full bg-gray-100 px-2 py-1 text-[11px] font-semibold leading-none text-gray-700">
                  {activeFilterCount || searchQuery.trim()
                    ? `${pagination.total.toLocaleString()} matching`
                    : `${pagination.total.toLocaleString()} on file`}
                </span>
                <form onSubmit={handleSearch} className="ml-auto flex flex-wrap items-center gap-2">
                  <input
                    type="text"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    placeholder="Search address, city, name..."
                    aria-label="Search properties"
                    className="h-[30px] w-64 max-w-full rounded-lg border-gray-300 py-0 text-[12.5px] text-gray-800 placeholder:text-gray-400 focus:border-primary-500 focus:ring-2 focus:ring-primary-500/25"
                  />
                  <Button type="submit" variant="outline" size="sm">
                    Search
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => fetchProperties(searchQuery)}
                  >
                    Refresh
                  </Button>
                </form>
              </div>
              {/* ===== UI1: the filter bar =====
                  His ask, verbatim: *"by property type, size, dollar amount, etc."* Type and city
                  are dropdowns built from the DATA (so a choice can never return nothing); size
                  and price are ranges. Every one is applied server-side against all 7,405 rows.
                  ⭐ `sm:grid-cols-2 lg:grid-cols-4` - on a phone each control takes a full row
                  rather than sharing one, the DOC3d finding (a `col-span` inside a one-column grid
                  creates an implicit second column, so nothing here spans). */}
              <div className="border-b border-gray-150 bg-gray-50 px-5 py-3.5">
                <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
                  <Select
                    label="Property type"
                    aria-label="Filter by property type"
                    value={filters.property_type}
                    onChange={(e) => setFilter('property_type', e.target.value)}
                    options={[
                      { value: '', label: 'All types' },
                      ...(facets?.propertyTypes ?? []).map((t) => ({
                        value: t.value,
                        label: `${titleCaseType(t.value)} (${t.count.toLocaleString()})`,
                      })),
                    ]}
                  />
                  <Select
                    label="City"
                    aria-label="Filter by city"
                    value={filters.city}
                    onChange={(e) => setFilter('city', e.target.value)}
                    options={[
                      { value: '', label: 'All cities' },
                      ...(facets?.cities ?? []).map((c) => ({
                        value: c.value,
                        label: `${c.value} (${c.count.toLocaleString()})`,
                      })),
                    ]}
                  />
                  <div>
                    <label className="mb-1.5 block text-[12.5px] font-medium text-gray-700" htmlFor="min-building-size">
                      Building size (SF)
                    </label>
                    <div className="flex items-center gap-1.5">
                      <input
                        id="min-building-size"
                        type="number"
                        inputMode="numeric"
                        min={0}
                        placeholder="Min"
                        aria-label="Minimum building size in square feet"
                        value={filters.min_building_size}
                        onChange={(e) => setFilter('min_building_size', e.target.value)}
                        className="h-[38px] w-full min-w-0 rounded-lg border-gray-300 text-[13px] text-gray-800 placeholder:text-gray-400 focus:border-primary-500 focus:ring-2 focus:ring-primary-500/25"
                      />
                      <span className="shrink-0 text-[12px] text-gray-400">to</span>
                      <input
                        type="number"
                        inputMode="numeric"
                        min={0}
                        placeholder="Max"
                        aria-label="Maximum building size in square feet"
                        value={filters.max_building_size}
                        onChange={(e) => setFilter('max_building_size', e.target.value)}
                        className="h-[38px] w-full min-w-0 rounded-lg border-gray-300 text-[13px] text-gray-800 placeholder:text-gray-400 focus:border-primary-500 focus:ring-2 focus:ring-primary-500/25"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="mb-1.5 block text-[12.5px] font-medium text-gray-700" htmlFor="min-price">
                      Sale price ($)
                    </label>
                    <div className="flex items-center gap-1.5">
                      <input
                        id="min-price"
                        type="number"
                        inputMode="numeric"
                        min={0}
                        placeholder="Min"
                        aria-label="Minimum sale price in dollars"
                        value={filters.min_price}
                        onChange={(e) => setFilter('min_price', e.target.value)}
                        className="h-[38px] w-full min-w-0 rounded-lg border-gray-300 text-[13px] text-gray-800 placeholder:text-gray-400 focus:border-primary-500 focus:ring-2 focus:ring-primary-500/25"
                      />
                      <span className="shrink-0 text-[12px] text-gray-400">to</span>
                      <input
                        type="number"
                        inputMode="numeric"
                        min={0}
                        placeholder="Max"
                        aria-label="Maximum sale price in dollars"
                        value={filters.max_price}
                        onChange={(e) => setFilter('max_price', e.target.value)}
                        className="h-[38px] w-full min-w-0 rounded-lg border-gray-300 text-[13px] text-gray-800 placeholder:text-gray-400 focus:border-primary-500 focus:ring-2 focus:ring-primary-500/25"
                      />
                    </div>
                  </div>
                </div>

                {/* ⭐ The row that says a filter is ON. Without it a narrowed table and an empty
                    one look identical, and the answer to "where did my properties go" is off
                    screen on a phone. */}
                {activeFilterCount > 0 && (
                  <div className="mt-3 flex flex-wrap items-center gap-x-3 gap-y-2 text-[12.5px] text-gray-600">
                    <span>
                      {activeFilterCount === 1 ? '1 filter' : `${activeFilterCount} filters`} applied
                      {totalOnFile ? ` · ${pagination.total.toLocaleString()} of ${totalOnFile.toLocaleString()} properties` : ''}
                    </span>
                    <button
                      type="button"
                      onClick={clearFilters}
                      className="font-semibold text-primary-500 underline-offset-[3px] hover:underline"
                    >
                      Clear filters
                    </button>
                  </div>
                )}

                {/* ⛔ Price and size live on the TRANSACTION and the building record, and not every
                    row carries one. Saying so is the difference between a filter that narrowed and
                    a database that looks empty. Measured 2026-09-07: 6,168 of 7,405 rows have a
                    sale price, 6,642 have a building size. */}
                {(filters.min_price || filters.max_price) && (
                  <p className="mt-2 text-[11.5px] text-gray-500">
                    A price filter can only match the 6,168 properties that carry a recorded sale.
                  </p>
                )}
                {(filters.min_building_size || filters.max_building_size) && (
                  <p className="mt-1 text-[11.5px] text-gray-500">
                    A size filter can only match the 6,642 properties that carry a building size.
                  </p>
                )}
              </div>

              {error && (
                <div className="mx-5 mt-4 rounded-lg border border-red-200 bg-red-50 p-3 text-[12.5px] text-red-700">
                  {error}
                </div>
              )}

              {loading ? (
                <div className="py-12 text-center">
                  <div className="mx-auto h-8 w-8 animate-spin rounded-full border-b-2 border-primary-500"></div>
                  <p className="mt-2 text-[12.5px] text-gray-500">Loading properties...</p>
                </div>
              ) : properties.length === 0 ? (
                /* ⛔ TWO DIFFERENT EMPTIES, TWO DIFFERENT ANSWERS. "Import some data" is the right
                   sentence for an empty database and the WRONG one for a filter that matched
                   nothing over 7,405 rows - it sends him to the CSV tab to solve a problem he
                   created with a dropdown. */
                activeFilterCount > 0 || searchQuery.trim() ? (
                  <p className="px-5 py-4 text-[12.5px] text-gray-500">
                    No properties match{searchQuery.trim() ? ' this search' : ''}
                    {activeFilterCount > 0 && searchQuery.trim() ? ' and' : ''}
                    {activeFilterCount > 0 ? (activeFilterCount === 1 ? ' this filter' : ' these filters') : ''}.
                    {activeFilterCount > 0 && (
                      <>
                        {' '}
                        <button type="button" onClick={clearFilters} className="font-semibold text-primary-500 hover:underline underline-offset-[3px]">
                          Clear filters
                        </button>{' '}
                        to see all {totalOnFile ? totalOnFile.toLocaleString() : ''} properties.
                      </>
                    )}
                  </p>
                ) : (
                  <p className="px-5 py-4 text-[12.5px] text-gray-500">
                    No properties found - import data from the{' '}
                    <button type="button" onClick={() => setActiveTab('upload')} className="font-semibold text-primary-500 hover:underline underline-offset-[3px]">
                      Bulk Upload (CSV) tab
                    </button>{' '}
                    to get started.
                  </p>
                )
              ) : (
                <>
                  {/* The design's grid table: uppercase 9.5px heads on #FAFAFA, hairline rows, tabular
                      numbers, the type as a category TAG, the price charcoal (never red). It scrolls
                      inside its own container on a narrow screen; the page does not. */}
                  <div className="overflow-x-auto">
                    <table className="w-full min-w-[960px] text-[13px]">
                      <thead className="border-b border-gray-150 bg-gray-50">
                        <tr className="text-[9.5px] font-bold uppercase leading-none tracking-[.12em] text-gray-500">
                          <th className="w-[84px] px-4 py-2.5 text-left"></th>
                          <th className="px-4 py-2.5 text-left">Address</th>
                          <th className="px-4 py-2.5 text-left">City</th>
                          <th className="px-4 py-2.5 text-left">Type</th>
                          <th className="px-4 py-2.5 text-right">Size (SF)</th>
                          <th className="px-4 py-2.5 text-right">Year Built</th>
                          <th className="px-4 py-2.5 text-right">Sale Price</th>
                          <th className="px-4 py-2.5 text-right">$/SF</th>
                          <th className="px-4 py-2.5 text-right">Cap Rate</th>
                          <th className="px-4 py-2.5 text-left">Source</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-100">
                        {properties.map((property) => (
                          <tr key={property.id} className="cursor-pointer hover:bg-gray-50" onClick={() => navigate(`/data-hub/${property.id}`)}>
                            <td className="px-4 py-2">
                              {property.primary_image_path ? (
                                <img
                                  src={`${SUPABASE_STORAGE_BASE}${property.primary_image_path}`}
                                  alt=""
                                  loading="lazy"
                                  className="h-10 w-[52px] rounded-lg bg-gray-100 object-cover"
                                />
                              ) : (
                                <div className="flex h-10 w-[52px] items-center justify-center rounded-lg bg-gray-100 text-gray-300">
                                  <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.8}><path strokeLinecap="round" strokeLinejoin="round" d="M4 16l4-4a3 3 0 014 0l4 4m-4-2l1.5-1.5a3 3 0 014 0L20 14m-8-8h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" /></svg>
                                </div>
                              )}
                            </td>
                            <td className="px-4 py-3">
                              <div className="max-w-[240px] truncate font-medium text-gray-800">
                                {property.address}
                              </div>
                            </td>
                            <td className="px-4 py-3 text-gray-700">
                              {property.city}, {property.state}
                            </td>
                            <td className="px-4 py-3">
                              <Tag>{property.property_type || 'Unknown'}</Tag>
                            </td>
                            <td className="px-4 py-3 text-right tabular-nums text-gray-700">
                              {formatNumber(property.building_size)}
                            </td>
                            <td className="px-4 py-3 text-right tabular-nums text-gray-700">
                              {property.year_built || '-'}
                            </td>
                            <td className={`px-4 py-3 text-right font-semibold tabular-nums ${property.latest_sale_price ? 'text-gray-800' : 'text-gray-400'}`}>
                              {formatCurrency(property.latest_sale_price)}
                            </td>
                            <td className="px-4 py-3 text-right tabular-nums text-gray-700">
                              {property.latest_price_per_sf ? `$${property.latest_price_per_sf.toFixed(0)}` : '-'}
                            </td>
                            <td className="px-4 py-3 text-right tabular-nums text-gray-700">
                              {property.latest_cap_rate ? `${property.latest_cap_rate.toFixed(2)}%` : '-'}
                            </td>
                            <td className="px-4 py-3">
                              <span className="text-[11.5px] capitalize text-gray-500">
                                {property.source || 'manual'}
                              </span>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>

                  {/* Pagination - OUTSIDE the scroll container, so it never scrolls away with the table */}
                  {pagination.total > pagination.limit && (
                    <div className="flex flex-wrap items-center justify-between gap-3 border-t border-gray-150 px-5 py-3.5">
                      <p className="text-[12.5px] tabular-nums text-gray-500">
                        Showing {pagination.offset + 1} - {Math.min(pagination.offset + pagination.limit, pagination.total)} of {pagination.total.toLocaleString()}
                      </p>
                      <div className="flex gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          disabled={pagination.offset === 0}
                          onClick={() => setPagination(prev => ({ ...prev, offset: Math.max(0, prev.offset - prev.limit) }))}
                        >
                          Previous
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          disabled={pagination.offset + pagination.limit >= pagination.total}
                          onClick={() => setPagination(prev => ({ ...prev, offset: prev.offset + prev.limit }))}
                        >
                          Next
                        </Button>
                      </div>
                    </div>
                  )}
                </>
              )}
            </Card>
          </div>
        )}

        {/* Verification Tab */}
        {activeTab === 'verification' && (
          <div className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle>Data Verification Queue</CardTitle>
                <p className="text-xs text-gray-400 mt-0.5">Records not verified in the past year land here.</p>
              </CardHeader>
              <CardContent>
                <p className="text-gray-600 mb-4">
                  Properties that haven't been verified in the past year will appear here.
                  Keep your data fresh by periodically reviewing and updating records.
                </p>
                <p className="text-xs text-gray-400 py-1">
                  No properties need verification - all your data is up to date.
                </p>
              </CardContent>
            </Card>
          </div>
        )}
      </div>

      {/* Add Property Modal - OO5c: through the one Dialog primitive. */}
      <Dialog
        open={showAddModal}
        onClose={() => setShowAddModal(false)}
        title="Add Property"
        size="lg"
        footer={<>
          <Button variant="outline" onClick={() => setShowAddModal(false)}>Cancel</Button>
          <Button onClick={handleAddProperty} disabled={addSaving}>
            {addSaving ? 'Creating...' : 'Create Property'}
          </Button>
        </>}
      >
              {addError && <div className="mb-4 p-3 bg-red-50 border border-red-200 text-red-700 rounded-lg text-sm">{addError}</div>}
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Address *</label>
                  <input className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" value={addForm.address} onChange={e => setAddForm(f => ({ ...f, address: e.target.value }))} placeholder="123 Main St" />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">City *</label>
                    <input className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" value={addForm.city} onChange={e => setAddForm(f => ({ ...f, city: e.target.value }))} placeholder="Ontario" />
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">State</label>
                      <input className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" value={addForm.state} onChange={e => setAddForm(f => ({ ...f, state: e.target.value }))} />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">ZIP</label>
                      <input className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" value={addForm.zip} onChange={e => setAddForm(f => ({ ...f, zip: e.target.value }))} />
                    </div>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Property Name</label>
                    <input className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" value={addForm.property_name} onChange={e => setAddForm(f => ({ ...f, property_name: e.target.value }))} />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Type</label>
                    <select className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" value={addForm.property_type} onChange={e => setAddForm(f => ({ ...f, property_type: e.target.value }))}>
                      <option value="">Select...</option>
                      <option value="industrial">Industrial</option>
                      <option value="office">Office</option>
                      <option value="retail">Retail</option>
                      <option value="multifamily">Multifamily</option>
                      <option value="land">Land</option>
                    </select>
                  </div>
                </div>
                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Building Size (SF)</label>
                    <input type="number" className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" value={addForm.building_size} onChange={e => setAddForm(f => ({ ...f, building_size: e.target.value }))} />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Year Built</label>
                    <input type="number" className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" value={addForm.year_built} onChange={e => setAddForm(f => ({ ...f, year_built: e.target.value }))} />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Owner</label>
                    <input className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" value={addForm.owner_name} onChange={e => setAddForm(f => ({ ...f, owner_name: e.target.value }))} />
                  </div>
                </div>
                <div className="border-t pt-4 mt-4">
                  <p className="text-sm font-medium text-gray-500 mb-3">Transaction (optional)</p>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Sale Price</label>
                      <input type="number" className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" value={addForm.sale_price} onChange={e => setAddForm(f => ({ ...f, sale_price: e.target.value }))} placeholder="0" />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Cap Rate (%)</label>
                      <input type="number" step="0.01" className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" value={addForm.cap_rate} onChange={e => setAddForm(f => ({ ...f, cap_rate: e.target.value }))} placeholder="5.50" />
                    </div>
                  </div>
                </div>
              </div>
      </Dialog>
    </Layout>
  );
}
